import { z } from "zod";

export const additionalTicketsSchema = z
  .array(
    z.object({
      eventTicketVariationID: z.string().min(1).max(100),
      recipientName: z.string().trim().min(1).max(100),
    }),
  )
  .min(1);
export const groupPurchaseSchema = z.object({
  additionalTickets: additionalTicketsSchema,
  quoteFingerprint: z.string().min(1).max(1000),
  idempotencyKey: z.string().min(1).max(100),
});
export const ticketSlotSchema = z.object({
  id: z.string().min(1).max(100),
  variationID: z.string().min(1).max(100),
  recipientName: z.string().max(100).default(""),
});
export type TicketSlot = z.infer<typeof ticketSlotSchema>;

type GroupTicketVariation = {
  id: string;
  available: boolean;
  position?: number;
  ticketsRemaining?: number | null;
  availableAfterID?: string | null;
  activatedAt?: string | null;
  checkoutStatus?: string;
};

// This availability belongs to the bag, not to the plan's shared inventory.
export function groupTicketVariations<T extends GroupTicketVariation>(
  variations: readonly T[],
  slots: readonly Pick<TicketSlot, "variationID">[],
) {
  const counts = new Map<string, number>();
  for (const slot of slots) {
    counts.set(slot.variationID, (counts.get(slot.variationID) ?? 0) + 1);
  }
  const filled = new Set<string>();
  return [...variations]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((variation) => {
      const available = variation.available || (
        variation.checkoutStatus === "UPCOMING" &&
        variation.activatedAt == null &&
        !!variation.availableAfterID &&
        filled.has(variation.availableAfterID) &&
        (variation.ticketsRemaining == null || variation.ticketsRemaining > 0)
      );
      if (
        available &&
        variation.ticketsRemaining != null &&
        variation.ticketsRemaining > 0 &&
        counts.get(variation.id) === variation.ticketsRemaining
      ) {
        filled.add(variation.id);
      }
      return { ...variation, available };
    });
}

// Quantity/discount review precedes guest names. These temporary quote labels
// never enter the draft or purchase command; payment re-quotes the actual names.
export function groupQuoteSelection(slots: TicketSlot[], purchaserSlotID: string) {
  const ownID = slots.some(slot => slot.id === purchaserSlotID) ? purchaserSlotID : slots[0]?.id ?? "";
  return namedGroupSelection(slots.map((slot, index) => ({
    ...slot,
    recipientName: slot.recipientName.trim() || `Ticket ${index + 1}`,
  })), ownID);
}

export function namedGroupSelection(
  slots: TicketSlot[],
  purchaserSlotID: string,
) {
  const purchaser = slots.find((slot) => slot.id === purchaserSlotID);
  if (!purchaser || new Set(slots.map((slot) => slot.id)).size !== slots.length)
    return null;
  if (slots.length === 1)
    return {
      eventTicketVariationID: purchaser.variationID,
      additionalTickets: [],
    };
  const additionalTickets = additionalTicketsSchema.safeParse(
    slots
      .filter((slot) => slot.id !== purchaserSlotID)
      .map((slot) => ({
        eventTicketVariationID: slot.variationID,
        recipientName: slot.recipientName,
      })),
  );
  return additionalTickets.success
    ? {
        eventTicketVariationID: purchaser.variationID,
        additionalTickets: additionalTickets.data,
      }
    : null;
}

export function groupSelectionAvailable(
  slots: TicketSlot[],
  plan: {
    allowGroupRSVPs?: boolean;
    maxTicketsPerPurchase?: number;
    ticketVariations: GroupTicketVariation[];
  },
) {
  if (
    !slots.length ||
    slots.length >
      (plan.allowGroupRSVPs ? (plan.maxTicketsPerPurchase ?? 1) : 1)
  )
    return false;
  const variations = groupTicketVariations(plan.ticketVariations, slots);
  return slots.every((slot) => {
    const variation = variations.find(
      (value) => value.id === slot.variationID,
    );
    const count = slots.filter(
      (value) => value.variationID === slot.variationID,
    ).length;
    return (
      !!variation?.available &&
      (variation.ticketsRemaining == null ||
        count <= variation.ticketsRemaining)
    );
  });
}

// Only a definitive pre-acceptance rejection permits a new command. An unknown
// response or idempotency conflict retains the original financial identity.
export function canReplacePurchaseAttempt(
  errorType: string | null | undefined,
) {
  return [
    "PROFILE_INCOMPLETE",
    "FORM_INCOMPLETE",
    "INVALID_PURCHASER_DETAILS",
    "PRICE_CHANGED",
    "TICKET_QUOTE_INVALID",
    "DISCOUNT_CODE_INVALID",
    "TICKET_VARIATION_UNAVAILABLE",
    "EVENT_FULL",
    "GROUP_RSVPS_DISABLED",
    "GROUP_SIZE_EXCEEDED",
    "INVALID_GROUP_SELECTION",
    "GROUP_DISCOUNT_CODES_DISABLED",
  ].includes(errorType ?? "");
}
